/* ============================================================
   Modulo: News rail engine — shared by section-news-marquee (nm)
   and section-news-wire (nw).

   Intentionally BEM-agnostic, like modulo-section-feature-scroll.js:
   it only touches neutral data- hooks, so both siblings share it
   verbatim and neither owns the namespace.

     [data-modulo-nr]           section root
       data-nr-speed="22"       velocity in PIXELS PER SECOND
       data-nr-dir="1|-1"       1 = content travels left, -1 = right
       data-nr-pause="1|0"      pause while hovered
     [data-modulo-nr-rail]      the scroller (overflow-x:auto)
       data-modulo-nr-ready="1" set once wired, so CSS can style the
                                no-JS state via :not([...ready="1"])

   WHY A REAL SCROLLER AND NOT A CSS KEYFRAME
   The locked interaction model is "auto-scroll, then hand control to
   the shopper permanently on any real intent". Driving native
   scrollLeft means the handed-over state IS a normal scroll container:
   free momentum, free touch, free keyboard, free scroll-snap. A
   transform-on-a-track marquee would have to reimplement all four.

   VELOCITY IS px/s, NEVER seconds-per-cycle.
   A duration-based loop makes apparent speed a function of card count.
   That is invisible on news-marquee's ragged widths and immediately
   wrong on news-wire, where uniform card width IS the treatment, so a
   7th story would silently slow the strip. Setting ids and var names
   freeze at the merge-base, so this had to be right in the first commit.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var mq = window.matchMedia;
  var REDUCE = mq && mq('(prefers-reduced-motion: reduce)').matches;
  // Touch devices never auto-scroll: there is no hover to pause with, and a
  // moving link is hostile to tap. They get a plain swipeable snap rail.
  var FINE = mq ? mq('(hover: hover) and (pointer: fine)').matches : false;

  var instances = [];

  function gapOf(el) {
    var s = getComputedStyle(el);
    var n = parseFloat(s.columnGap || s.gap || '0');
    return isNaN(n) ? 0 : n;
  }

  /* aria-hidden clones must leave the tab order. Cards are <a>, so a
     focusable clone is an off-screen keyboard trap — the bug
     section-photo-marquee ships today. Also strip the theme-editor
     attributes, or click-to-select in the editor targets a clone. */
  function neutralize(node) {
    node.setAttribute('aria-hidden', 'true');
    node.removeAttribute('role');
    node.removeAttribute('id');
    node.removeAttribute('data-shopify-editor-block');
    if (node.tagName === 'A') node.setAttribute('tabindex', '-1');
    var f = node.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    for (var i = 0; i < f.length; i++) {
      f[i].setAttribute('tabindex', '-1');
      f[i].removeAttribute('id');
      f[i].removeAttribute('data-shopify-editor-block');
    }
    var ids = node.querySelectorAll('[id], [data-shopify-editor-block]');
    for (var j = 0; j < ids.length; j++) {
      ids[j].removeAttribute('id');
      ids[j].removeAttribute('data-shopify-editor-block');
    }
  }

  function init(root) {
    var rail = root.querySelector('[data-modulo-nr-rail]');
    if (!rail || rail.getAttribute('data-modulo-nr-ready') === '1') return;

    var originals = Array.prototype.slice.call(rail.children);
    if (!originals.length) return;

    var speed = parseFloat(root.getAttribute('data-nr-speed'));
    if (isNaN(speed) || speed <= 0) speed = 24;
    var dir = root.getAttribute('data-nr-dir') === '-1' ? -1 : 1;
    var pauseOnHover = root.getAttribute('data-nr-pause') !== '0';

    var inst = {
      root: root,
      rail: rail,
      originals: originals,
      speed: speed,
      dir: dir,
      pauseOnHover: pauseOnHover,
      rtl: getComputedStyle(rail).direction === 'rtl',
      cycle: 0,
      pos: 0,
      raf: null,
      last: 0,
      hovered: false,
      visible: true,
      // "live" = the shopper has taken over; auto-scroll never resumes.
      live: REDUCE || !FINE,
      cloned: false,
      expected: 0
    };

    rail.setAttribute('data-modulo-nr-ready', '1');
    wire(inst);
    instances.push(inst);
    return inst;
  }

  /* Clone the original set until the scroller holds one full cycle plus a
     viewport, so wrapping at `cycle` never exposes a gap. */
  function fill(inst) {
    if (inst.cloned) return;
    var gap = gapOf(inst.rail);
    var w = 0;
    for (var i = 0; i < inst.originals.length; i++) {
      w += inst.originals[i].getBoundingClientRect().width + gap;
    }
    if (w <= 0) return; // layout not ready yet
    inst.cycle = w;

    var need = inst.cycle + (inst.rail.offsetWidth || 0);
    var guard = 0;
    while (inst.rail.scrollWidth < need && guard < 12) {
      for (var j = 0; j < inst.originals.length; j++) {
        var c = inst.originals[j].cloneNode(true);
        neutralize(c);
        inst.rail.appendChild(c);
      }
      guard++;
    }
    inst.cloned = true;
  }

  function apply(inst) {
    var v = inst.rtl ? -inst.pos : inst.pos;
    inst.rail.scrollLeft = v;
    inst.expected = inst.rail.scrollLeft;
  }

  function frame(now) {
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (inst.raf !== 1) continue;
      step(inst, now);
    }
    var any = false;
    for (var k = 0; k < instances.length; k++) if (instances[k].raf === 1) any = true;
    rafId = any ? requestAnimationFrame(frame) : null;
  }

  var rafId = null;
  function pump() {
    if (rafId === null) rafId = requestAnimationFrame(frame);
  }

  function step(inst, now) {
    if (!inst.last) inst.last = now;
    var dt = (now - inst.last) / 1000;
    inst.last = now;
    if (dt > 0.05) dt = 0.05; // a backgrounded tab must not teleport the rail

    if (inst.hovered && inst.pauseOnHover) return;

    /* The shopper scrolled without tripping an intent listener (a trackpad
       fling, a screen-reader move). Compare against what we last wrote —
       a boolean flag desyncs because scroll events coalesce, so N writes
       can produce one event.

       A UA CLAMP IS NOT INTENT. If the content shrank (resize, a font landing,
       an image resolving) the browser pins scrollLeft to the new maximum, which
       reads as a large delta and would permanently kill the rail through no
       action of the shopper's. Re-seat against reality instead. */
    if (Math.abs(inst.rail.scrollLeft - inst.expected) > 2) {
      var max = inst.rail.scrollWidth - inst.rail.clientWidth;
      var at = Math.abs(inst.rail.scrollLeft);
      if (at <= 1 || at >= max - 1) {
        inst.pos = at;
        apply(inst);
      } else {
        takeOver(inst);
        return;
      }
    }

    /* Position is authoritative in this float and is NEVER read back from
       scrollLeft, whose sub-pixel rounding would silently make the rail
       slower than configured and stall below 0.5px/frame. */
    inst.pos += inst.speed * inst.dir * dt;
    if (inst.pos >= inst.cycle) inst.pos -= inst.cycle;
    if (inst.pos < 0) inst.pos += inst.cycle;
    apply(inst);
  }

  function start(inst) {
    if (inst.live || inst.raf === 1) return;
    if (!inst.cloned) fill(inst);
    if (!inst.cycle) return;
    inst.last = 0;
    inst.raf = 1;
    pump();
  }

  function stop(inst) {
    inst.raf = null;
  }

  /* Handover is permanent. Snap is attached HERE and only here: a
     scroll-snap container fights a per-frame programmatic write, and the
     rail judders as the UA re-snaps every frame. */
  function takeOver(inst) {
    if (inst.live) return;
    inst.live = true;
    stop(inst);
    inst.root.classList.add('modulo-nr--live');
  }

  function wire(inst) {
    var root = inst.root;
    var rail = inst.rail;

    if (inst.live) {
      root.classList.add('modulo-nr--live');
      return; // reduced motion / touch: a plain snap rail, nothing to drive
    }

    rail.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      inst.hovered = true;
    });
    rail.addEventListener('pointerleave', function () {
      inst.hovered = false;
      inst.last = 0; // don't bill the paused time to the next frame
    });

    // A keyboard user needs the rail to stop for good, not just while focused.
    rail.addEventListener('focusin', function () { takeOver(inst); });

    rail.addEventListener('wheel', function () { takeOver(inst); }, { passive: true });
    rail.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'Home' || k === 'End' ||
          k === 'PageUp' || k === 'PageDown') takeOver(inst);
    });

    /* Pointer drag → native scrollLeft. Touch is excluded so the browser's
       own momentum scrolling wins. */
    var down = false, anchored = false, sx = 0, sl = 0, moved = 0, suppressClick = false;
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true; anchored = false; moved = 0; sx = e.clientX;
      root.classList.add('modulo-nr--dragging');
    });
    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - sx;
      if (Math.abs(dx) <= 3) return;
      if (!anchored) {
        /* Anchor at the moment of handover, NOT at pointerdown: the rAF loop
           keeps writing scrollLeft between press and threshold, so a position
           captured on press is already stale and the first drag jumps the rail
           backwards by however far it travelled in between. */
        takeOver(inst);
        anchored = true;
        sx = e.clientX;
        sl = rail.scrollLeft;
        return;
      }
      rail.scrollLeft = sl - dx;
      moved = Math.abs(dx);
    });
    function end() {
      down = false;
      anchored = false;
      /* Latch the decision at pointerup and clear the distance immediately.
         Leaving `moved` set means a drag released outside the rail keeps the
         suppressor armed and swallows the NEXT activation of a card. */
      suppressClick = moved > 4;
      moved = 0;
      root.classList.remove('modulo-nr--dragging');
    }
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);
    // Capture phase: without this every drag navigates to the dragged card.
    rail.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); }
      suppressClick = false;
    }, true);

    /* Offscreen + hidden-tab pause are requirements, not optimisations:
       news-wire's stated home is directly above the footer, so it is
       offscreen for most of a session by design. */
    if ('IntersectionObserver' in window) {
      inst.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          inst.visible = entries[i].isIntersecting;
          if (inst.visible && !document.hidden) start(inst); else stop(inst);
        }
      }, { threshold: 0 });
      inst.io.observe(root);
    } else {
      start(inst);
    }
  }

  function onVisibility() {
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (document.hidden || !inst.visible) stop(inst); else start(inst);
    }
  }
  document.addEventListener('visibilitychange', onVisibility);

  var rt = null;
  window.addEventListener('resize', function () {
    if (rt) clearTimeout(rt);
    rt = setTimeout(function () {
      for (var i = 0; i < instances.length; i++) {
        var inst = instances[i];
        if (inst.live) continue;
        inst.cloned = false;
        fill(inst);
        if (inst.cycle && inst.pos >= inst.cycle) inst.pos = inst.pos % inst.cycle;
        // Rewrite `expected` from the post-clamp scrollLeft, or the next frame
        // sees the resize's own clamp as shopper intent and stops for good.
        if (inst.cycle) apply(inst);
        // Re-measuring can be what first produces a usable cycle, so a rail
        // that could not measure at boot has to be given its start here.
        if (inst.visible && !document.hidden) start(inst);
      }
    }, 150);
  });

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-modulo-nr]');
    for (var i = 0; i < roots.length; i++) {
      var inst = init(roots[i]);
      if (inst && !inst.live) start(inst);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  /* Images settle after load and change the measured cycle. This is also the
     recovery path for a rail whose cards had no width at boot: fill() bails
     when it measures zero, leaving cycle at 0, and start() refuses to run
     without a cycle — so the restart below is load-bearing, not a nicety. */
  window.addEventListener('load', function () {
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (inst.live) continue;
      inst.cloned = false;
      fill(inst);
      if (inst.cycle) apply(inst);
      if (inst.visible && !document.hidden) start(inst);
    }
  });

  document.addEventListener('shopify:section:load', function (e) {
    boot((e && e.target) || document);
  });

  /* A CSS animation dies with its node; a rAF loop does not. Without this
     the theme editor accumulates one orphaned loop per edit. */
  document.addEventListener('shopify:section:unload', function (e) {
    var target = e && e.target;
    for (var i = instances.length - 1; i >= 0; i--) {
      if (target && target.contains && target.contains(instances[i].root)) {
        stop(instances[i]);
        if (instances[i].io) instances[i].io.disconnect();
        instances.splice(i, 1);
      }
    }
  });
})();
